// «Ingreso de inventario» (brief 26-oct-2026, paso 1): las reglas de `captura.html` pasadas al sistema.
// Puro y compartido: la pantalla lo usa para decir qué falta mientras se teclea, y el servidor lo usa al
// «Subir repisa» para armar la carga que entra por el importador de siempre (dry-run → commit → deshacer).
//
// Tramo 10 (Kevin, 27-oct): un renglón = UN producto con hasta tres presentaciones (unidad, blíster y caja,
// cada una con su precio) y VARIOS lotes, cada uno con su conteo (cajas + blísteres + sueltas), su número
// y su vencimiento. La fluoxetina de dos lotes es un solo renglón, no dos productos. Un lote que aparece
// cuando el producto ya subió entra por Recepción: «deshacer esta carga» borra los lotes del producto en
// la botica, así que dos cargas del mismo producto no se podrían deshacer por separado.
import type { BlisterImportable, ProductoImportable } from "../catalogo/importar";
import { MAX_UBICACION } from "../catalogo/buscador";
import { normalizarNombre } from "../catalogo/normalizar";
import { sinIgvDmDesdeVentaPublicaDm } from "../calculos/dinero";
import { MAX_PRECIO_DM, parseFecha, parseMonedaDm } from "../csv/valores";

/** Un lote del renglón: lo tecleado tal cual. */
export type LoteIngreso = {
  id: string;
  lote: string;
  vence: string;
  cajas: string;
  blisteres: string;
  sueltas: string;
};

/** Lo que el servidor guarda de cada renglón: lo tecleado tal cual (texto), para no perder nada a medias. */
export type ItemIngreso = {
  id: string;
  gtin: string | null;
  nombre: string;
  principio_activo: string | null;
  laboratorio: string | null;
  presentacion: string | null;
  forma: string | null;
  /** El código está en el catálogo nacional (catalogo_maestro): nombre y principio activo vinieron de ahí. */
  en_maestro: boolean;
  /** El producto ya tiene inventario en esta botica: subirlo no suma nada (para sumar, Recepción). */
  ya_en_botica: boolean;
  factor: string; // unidades que trae la caja
  suelto: boolean | null; // ¿se vende por unidad suelta?
  blister: boolean | null; // ¿viene en blísteres?
  /** Unidades por blíster. Vacío = las de siempre (POR_BLISTER_SUGERIDO); se cambia solo si hace falta. */
  por_blister: string;
  precio_unidad: string;
  precio_blister: string;
  /** Opcional si se vende por blíster o por unidad: vacío = la caja cuenta, pero no se vende entera. */
  precio_caja: string;
  requiere_receta: boolean | null; // null = todavía no lo dijo
  /** Al menos uno. En el orden en que se agregaron: el lector suma al último. */
  lotes: LoteIngreso[];
};

export type CampoIngreso =
  | "nombre" | "factor" | "suelto" | "blister" | "por_blister" | "precio_unidad" | "precio_blister" | "precio_caja"
  | "cajas" | "lote" | "vence" | "requiere_receta";

export type AnalisisItem = {
  problemas: string[]; // con uno solo, el renglón no sube
  avisos: string[]; // sube igual
  unidades: number; // unidades base que entran (todos los lotes)
  lotes: { id: string; unidades: number; venceIso: string | null }[];
  /** `loteId`: el campo es de ese lote (cajas, lote, vence). */
  siguiente: { campo: CampoIngreso; texto: string; loteId?: string } | null;
};

export const DIAS_VENCE_PRONTO = 90;
/** Lo que trae un blíster casi siempre. Aciclovir (5) o levofloxacino (7) lo cambian. */
export const POR_BLISTER_SUGERIDO = 10;

// ---------------- códigos ----------------
function digitoOk(c: string): boolean {
  const d = c.split("").map(Number);
  const chk = d.pop()!;
  let s = 0;
  d.reverse().forEach((x, i) => (s += x * (i % 2 === 0 ? 3 : 1)));
  return (10 - (s % 10)) % 10 === chk;
}

/** null si el código de barras está bien; si no, por qué (en palabras de mostrador). */
export function revisarCodigo(c: string): string | null {
  if (/e\+/i.test(c)) return `código dañado: viene en notación científica (${c})`;
  if (!/^\d+$/.test(c)) return `el código tiene caracteres que no son números (${c})`;
  if (c.length < 8) return `código de menos de 8 dígitos (${c}): parece dañado`;
  if (![8, 12, 13, 14].includes(c.length)) return `un código de barras tiene 8, 12, 13 o 14 dígitos; este tiene ${c.length}`;
  if (!digitoOk(c)) return `el último dígito no cuadra (${c}): se leyó o se escribió mal`;
  return null;
}

/** Variantes con que el mismo producto puede estar guardado (12↔13↔14 dígitos con cero adelante). */
export function variantesGtin(c: string): string[] {
  const v = [c];
  if (c.length === 14 && c[0] === "0") v.push(c.slice(1));
  if (c.length === 13 || c.length === 12) v.push("0" + c);
  if (c.length === 13 && c[0] === "0") v.push(c.slice(1));
  return v;
}

/** Código 2D GS1 (si el lector lo lee): 01 = producto, 17 = vencimiento AAMMDD, 10 = lote. */
export function leerGs1(crudo: string): { gtin: string; vence?: string; lote?: string } | null {
  let t = crudo.replace(/^\](d2|C1|Q3)/, "");
  if (!/^01\d{14}/.test(t)) return null;
  const GS = String.fromCharCode(29);
  const out: { gtin: string; vence?: string; lote?: string } = { gtin: t.slice(2, 16).replace(/^0/, "") };
  t = t.slice(16);
  while (t.length) {
    if (t[0] === GS) {
      t = t.slice(1);
      continue;
    }
    const ai = t.slice(0, 2);
    if (["11", "15", "17"].includes(ai)) {
      const v = t.slice(2, 8);
      if (!/^\d{6}$/.test(v)) break;
      if (ai === "17") {
        const y = 2000 + Number(v.slice(0, 2));
        const m = Number(v.slice(2, 4));
        const d = Number(v.slice(4, 6));
        const fin = new Date(Date.UTC(y, m, 0)).getUTCDate();
        out.vence = `${y}-${String(m).padStart(2, "0")}-${String(d === 0 ? fin : d).padStart(2, "0")}`;
      }
      t = t.slice(8);
      continue;
    }
    if (ai === "10" || ai === "21") {
      const fin = t.indexOf(GS, 2);
      const v = fin === -1 ? t.slice(2) : t.slice(2, fin);
      if (ai === "10") out.lote = v;
      t = fin === -1 ? "" : t.slice(fin + 1);
      continue;
    }
    break;
  }
  return out;
}

/** Lo que leyó el lector → el código del producto (+ lote y vencimiento si vino un 2D). */
export function leerEscaneo(crudo: string): { codigo: string; lote?: string; vence?: string; error: string | null } {
  const limpio = String(crudo ?? "").trim();
  const gs1 = leerGs1(limpio);
  const codigo = gs1 ? gs1.gtin : limpio.replace(/\s+/g, "");
  return { codigo, ...(gs1?.lote ? { lote: gs1.lote } : {}), ...(gs1?.vence ? { vence: gs1.vence } : {}), error: revisarCodigo(codigo) };
}

// ---------------- dinero, fechas y números ----------------
export type Leido<T> = { vacio: true } | { error: string } | ({ vacio?: false } & T);

/** Precio al público en soles («12.50», «S/ 12,50») → diezmilésimas y el texto normalizado «12.50». */
export function leerPrecio(txt: string): Leido<{ dm: number; texto: string }> {
  const s = String(txt ?? "").trim().replace(/^s\/\.?\s*/i, "");
  if (!s) return { vacio: true };
  if (!/^\d+([.,]\d{1,2})?$/.test(s)) return { error: `«${txt}»: escribe el precio con punto y hasta 2 decimales (ej. 12.50)` };
  const dm = parseMonedaDm(s);
  if (dm === null) return { error: `«${txt}» no es un precio válido` };
  if (dm <= 0) return { error: "el precio tiene que ser mayor que 0" };
  if (dm > MAX_PRECIO_DM) return { error: `«${txt}» es demasiado alto (máximo S/ 100,000)` };
  const soles = Math.floor(dm / 10000);
  const cent = Math.round((dm % 10000) / 100);
  return { dm, texto: `${soles}.${String(cent).padStart(2, "0")}` };
}

/** Vencimiento como lo escribe la caja: «03/2027», «03/27», «0327», «2027-03» → último día del mes; o una fecha completa. */
export function leerVence(txt: string): Leido<{ iso: string }> {
  const s = String(txt ?? "").trim();
  if (!s) return { vacio: true };
  const finDeMes = (y: number, m: number) => `${y}-${String(m).padStart(2, "0")}-${String(new Date(Date.UTC(y, m, 0)).getUTCDate()).padStart(2, "0")}`;
  const valMes = (y: number, mes: number): Leido<{ iso: string }> =>
    mes < 1 || mes > 12 || y < 2000 || y > 2100 ? { error: `«${txt}» no es una fecha válida` } : { iso: finDeMes(y, mes) };
  let m: RegExpExecArray | null;
  if ((m = /^(\d{1,2})[/\-.](\d{4})$/.exec(s))) return valMes(+m[2]!, +m[1]!);
  if ((m = /^(\d{1,2})[/\-.](\d{2})$/.exec(s))) return valMes(2000 + +m[2]!, +m[1]!);
  if ((m = /^(\d{2})(\d{2})$/.exec(s))) return valMes(2000 + +m[2]!, +m[1]!);
  if ((m = /^(\d{4})-(\d{1,2})$/.exec(s))) return valMes(+m[1]!, +m[2]!);
  const iso = parseFecha(s);
  if (iso) return { iso };
  return { error: `«${txt}»: escribe el vencimiento como mes/año (03/2027) o día/mes/año` };
}

/** Entero ≥0 tecleado; vacío = 0; null si no es un número entero. */
export function leerEntero(txt: string): number | null {
  const s = String(txt ?? "").trim();
  if (s === "") return 0;
  if (!/^\d+$/.test(s)) return null;
  const n = Number(s);
  return Number.isSafeInteger(n) ? n : null;
}

const fechaCorta = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
const tiene = <T>(l: Leido<T>): l is { vacio?: false } & T => !("vacio" in l && l.vacio) && !("error" in l);
const errorDe = <T>(l: Leido<T>): string | null => ("error" in l ? l.error : null);
const esVacio = <T>(l: Leido<T>): boolean => "vacio" in l && l.vacio === true;

/** Unidades por blíster: vacío = las de siempre. null = lo tecleado no es un entero. */
export function porBlister(it: Pick<ItemIngreso, "por_blister">): number | null {
  return it.por_blister.trim() === "" ? POR_BLISTER_SUGERIDO : leerEntero(it.por_blister);
}

/**
 * Cuántas unidades base vale cada cosa que se cuenta, y en qué se cuenta el stock. Se vende suelto o viene
 * en blísteres → la base es la tableta (una caja = `factor`, un blíster = `porBlister`). Si no → la base es
 * la caja entera (jarabe, crema, la caja que solo se vende cerrada). null = falta lo que trae la caja.
 */
export function moldeItem(it: Pick<ItemIngreso, "factor" | "suelto" | "blister" | "por_blister">): { cuentaPor: "unidad" | "caja"; caja: number; blister: number | null } | null {
  const f = leerEntero(it.factor);
  if (f === null || f < 1) return null;
  if (f > 1 && (it.suelto === true || it.blister === true)) {
    return { cuentaPor: "unidad", caja: f, blister: it.blister === true ? porBlister(it) : null };
  }
  return { cuentaPor: "caja", caja: 1, blister: null };
}

type ParaContar = Pick<ItemIngreso, "factor" | "suelto" | "blister" | "por_blister">;

/** Unidades base de un lote: cajas × lo que trae la caja + blísteres × lo que trae el blíster + sueltas. */
export function unidadesLote(it: ParaContar, l: Pick<LoteIngreso, "cajas" | "blisteres" | "sueltas">): number {
  const m = moldeItem(it);
  if (!m) return 0;
  const cajas = leerEntero(l.cajas) ?? 0;
  const blisteres = m.blister ? (leerEntero(l.blisteres) ?? 0) : 0;
  const sueltas = m.cuentaPor === "unidad" && it.suelto === true ? (leerEntero(l.sueltas) ?? 0) : 0;
  return cajas * m.caja + blisteres * (m.blister ?? 0) + sueltas;
}

/** Unidades base del renglón: la suma de sus lotes. */
export function unidadesItem(it: ParaContar & Pick<ItemIngreso, "lotes">): number {
  return it.lotes.reduce((s, l) => s + unidadesLote(it, l), 0);
}

/** El código del renglón va a la caja (no a la tableta) cuando el stock se cuenta por unidad. */
const codigoEsDeLaCaja = (it: ItemIngreso): boolean => moldeItem(it)?.cuentaPor === "unidad";
const claveLote = (s: string) => s.trim().toUpperCase();

/** Qué le falta o tiene mal a un renglón. `todos` = los renglones de la repisa (para los repetidos). */
export function analizarItem(it: ItemIngreso, todos: ItemIngreso[], hoy: string): AnalisisItem {
  const problemas: string[] = [];
  const avisos: string[] = [];
  if (!it.nombre.trim()) problemas.push("falta el nombre");
  if (it.gtin) {
    const e = revisarCodigo(it.gtin);
    if (e) problemas.push(e);
  }

  // ---- lo que trae la caja ----
  const f = leerEntero(it.factor);
  if (f === null || f < 1) problemas.push("falta cuántas unidades trae la caja");
  if (f !== null && f > 1 && it.suelto === null) problemas.push("falta decir si se vende suelto");
  if (f !== null && f > 1 && it.blister === null) problemas.push("falta decir si viene en blísteres");
  if (it.suelto === true && f !== null && f < 2) problemas.push("para vender suelto, la caja tiene que traer 2 unidades o más");
  if (it.blister === true && f !== null && f < 2) problemas.push("una caja de 1 unidad no viene en blísteres");
  const pb = it.blister === true ? porBlister(it) : null;
  if (it.blister === true && f !== null && f >= 2) {
    if (pb === null || pb < 2) problemas.push("las unidades por blíster tienen que ser un número entero de 2 o más");
    else if (pb === f) problemas.push("la caja trae un solo blíster: marca No en «¿Viene en blísteres?»");
    else if (pb > f) problemas.push(`un blíster de ${pb} no cabe en una caja de ${f}: revisa las unidades`);
    else if (f % pb !== 0) problemas.push(`una caja de ${f} no se reparte en blísteres de ${pb}: revisa las unidades`);
  }
  const m = moldeItem(it);
  const blisterOk = it.blister === true && pb !== null && f !== null && pb >= 2 && pb < f && f % pb === 0;

  // ---- precios: unidad, blíster y caja ----
  const pu = leerPrecio(it.precio_unidad);
  const pbl = leerPrecio(it.precio_blister);
  const pc = leerPrecio(it.precio_caja);
  if (it.suelto === true) {
    if (esVacio(pu)) problemas.push("falta el precio de cada unidad");
    else if (errorDe(pu)) problemas.push(`precio de la unidad: ${errorDe(pu)}`);
  }
  if (it.blister === true) {
    if (esVacio(pbl)) problemas.push("falta el precio del blíster");
    else if (errorDe(pbl)) problemas.push(`precio del blíster: ${errorDe(pbl)}`);
  }
  // La caja solo es obligatoria si es lo único que se vende.
  const cajaObligatoria = m?.cuentaPor !== "unidad";
  if (esVacio(pc)) {
    if (cajaObligatoria) problemas.push("falta el precio de la caja");
  } else if (errorDe(pc)) problemas.push(`precio de la caja: ${errorDe(pc)}`);
  const u = it.suelto === true && tiene(pu) ? pu.dm : null;
  const b = it.blister === true && tiene(pbl) ? pbl.dm : null;
  const c = tiene(pc) ? pc.dm : null;
  if (u !== null && b !== null && u > b) problemas.push("el precio de una unidad es mayor que el del blíster: ¿están al revés?");
  if (b !== null && c !== null && b > c) problemas.push("el precio del blíster es mayor que el de la caja: ¿están al revés?");
  if (u !== null && c !== null && u > c) problemas.push("el precio de una unidad es mayor que el de la caja: ¿están al revés?");
  if (u !== null && b !== null && blisterOk && u * pb! < b) avisos.push("por unidad sale más barato que el blíster");
  if (b !== null && c !== null && blisterOk && b * (f! / pb!) < c) avisos.push("por blíster sale más barato que la caja entera");
  if (u !== null && c !== null && f !== null && f > 1 && u * f < c) avisos.push("suelto sale más barato que la caja entera");

  // ---- lotes: cada uno con su conteo, su número y su vencimiento ----
  const lotes: AnalisisItem["lotes"] = [];
  if (it.lotes.length === 0) problemas.push("falta el lote");
  const varios = it.lotes.length > 1;
  const vistos = new Set<string>();
  it.lotes.forEach((l, i) => {
    const pre = varios ? `lote ${i + 1}: ` : "";
    const cajas = leerEntero(l.cajas);
    const blisteres = leerEntero(l.blisteres);
    const sueltas = leerEntero(l.sueltas);
    if (cajas === null) problemas.push(`${pre}las cajas tienen que ser un número entero`);
    if (blisteres === null) problemas.push(`${pre}los blísteres tienen que ser un número entero`);
    if (sueltas === null) problemas.push(`${pre}las sueltas tienen que ser un número entero`);
    if (it.blister !== true && blisteres) problemas.push(`${pre}hay blísteres, pero el producto no viene en blísteres`);
    if (it.suelto !== true && sueltas) problemas.push(`${pre}hay sueltas, pero el producto no se vende suelto`);
    const unidades = cajas !== null && blisteres !== null && sueltas !== null ? unidadesLote(it, l) : 0;
    if (cajas !== null && blisteres !== null && sueltas !== null && unidades <= 0) problemas.push(`${pre}no tiene unidades contadas`);
    if (it.suelto === true && sueltas) {
      if (blisterOk && sueltas >= pb!) avisos.push(`${pre}${sueltas} sueltas son un blíster o más`);
      else if (f !== null && f > 1 && sueltas >= f) avisos.push(`${pre}${sueltas} sueltas son una caja o más`);
    }
    if (blisterOk && blisteres && blisteres >= f! / pb!) avisos.push(`${pre}${blisteres} blísteres son una caja o más`);
    const lote = l.lote.trim();
    if (!lote) problemas.push(`${pre}falta el número de lote`);
    else if (lote.length > 60) problemas.push(`${pre}número de lote de más de 60 caracteres`);
    else if (vistos.has(claveLote(lote))) problemas.push(`el lote ${lote} está dos veces: júntalo en uno solo`);
    else vistos.add(claveLote(lote));
    const v = leerVence(l.vence);
    let venceIso: string | null = null;
    if (esVacio(v)) problemas.push(`${pre}falta el vencimiento`);
    else if (errorDe(v)) problemas.push(`${pre}${errorDe(v)}`);
    else if (tiene(v)) {
      venceIso = v.iso;
      if (v.iso < hoy) problemas.push(`${pre}vencido (${fechaCorta(v.iso)}): se aparta y no se carga`);
      else if ((Date.parse(v.iso) - Date.parse(hoy)) / 86_400_000 <= DIAS_VENCE_PRONTO) avisos.push(`${pre}vence pronto (${fechaCorta(v.iso)})`);
    }
    lotes.push({ id: l.id, unidades, venceIso });
  });

  if (it.requiere_receta === null) problemas.push("falta decir si pide receta");
  // Sin código en la base (no trae código, o el código es de la caja) se reconoce por el nombre: dos
  // renglones con el mismo nombre serían el mismo producto para el importador.
  if (!it.gtin || codigoEsDeLaCaja(it)) {
    const nn = normalizarNombre(it.nombre);
    if (nn && todos.some((o) => o.id !== it.id && (!o.gtin || codigoEsDeLaCaja(o)) && normalizarNombre(o.nombre) === nn)) {
      problemas.push("hay otro producto con el mismo nombre: cámbialo (por ejemplo, agrega el laboratorio)");
    }
  }
  if (it.gtin && todos.some((o) => o.id !== it.id && o.gtin === it.gtin)) {
    problemas.push("el mismo código está en otro renglón de esta repisa: agrega ahí este lote y quita este renglón");
  }
  if (it.gtin && !it.en_maestro) avisos.push("no está en el catálogo nacional: el nombre lo pusiste tú");
  if (it.ya_en_botica) avisos.push("ya está cargado en esta botica: no se suma; para sumar unidades usa Recepción");
  const unidades = lotes.reduce((s, l) => s + l.unidades, 0);
  return { problemas, avisos, unidades, lotes, siguiente: siguienteCampo(it) };
}

/** El primer campo que le falta, con la instrucción que se muestra arriba (como captura.html). */
export function siguienteCampo(it: ItemIngreso): AnalisisItem["siguiente"] {
  const f = leerEntero(it.factor);
  if (!it.nombre.trim()) return { campo: "nombre", texto: "Escribe el nombre del producto" };
  if (f === null || f < 1) return { campo: "factor", texto: "¿Cuántas unidades trae la caja?" };
  // Un «Sí» que quedó de antes de corregir la caja a 1 unidad: se pide el «No» (el botón sigue activo).
  if (f < 2 && it.suelto === true) return { campo: "suelto", texto: "Una caja de 1 unidad no se vende suelta: marca No" };
  if (f < 2 && it.blister === true) return { campo: "blister", texto: "Una caja de 1 unidad no viene en blísteres: marca No" };
  if (f > 1 && it.suelto === null) return { campo: "suelto", texto: "¿Se vende suelto?" };
  if (f > 1 && it.blister === null) return { campo: "blister", texto: "¿Viene en blísteres?" };
  if (it.blister === true) {
    const pb = porBlister(it);
    if (pb === null || pb < 2 || pb >= f || f % pb !== 0) return { campo: "por_blister", texto: "Revisa cuántas unidades trae cada blíster" };
  }
  if (it.suelto === true && !tiene(leerPrecio(it.precio_unidad))) return { campo: "precio_unidad", texto: "Escribe el precio de cada unidad" };
  if (it.blister === true && !tiene(leerPrecio(it.precio_blister))) return { campo: "precio_blister", texto: "Escribe el precio del blíster" };
  const pc = leerPrecio(it.precio_caja);
  if (moldeItem(it)?.cuentaPor !== "unidad" ? !tiene(pc) : !!errorDe(pc)) return { campo: "precio_caja", texto: "Escribe el precio de la caja" };
  const varios = it.lotes.length > 1;
  for (const [i, l] of it.lotes.entries()) {
    const de = varios ? ` del lote ${i + 1}` : "";
    if (unidadesLote(it, l) <= 0) return { campo: "cajas", loteId: l.id, texto: `¿Cuántas cajas contaste${de}?` };
    if (!l.lote.trim()) return { campo: "lote", loteId: l.id, texto: `Escribe el número de lote${varios ? ` ${i + 1}` : ""}` };
    if (!tiene(leerVence(l.vence))) return { campo: "vence", loteId: l.id, texto: `Escribe el vencimiento${de} (mes/año)` };
  }
  if (it.requiere_receta === null) return { campo: "requiere_receta", texto: "¿Pide receta?" };
  return null;
}

/** El nombre que se propone desde el catálogo nacional (nombre + concentración + forma, sin repetir). */
export function nombreDesdeMaestro(m: { nombre: string; concentracion?: string | null; forma?: string | null }): string {
  let n = m.nombre.trim();
  const low = () => n.toLowerCase();
  if (m.concentracion && !low().includes(m.concentracion.toLowerCase())) n += ` ${m.concentracion}`;
  if (m.forma && !low().includes(m.forma.toLowerCase())) n += ` ${m.forma}`;
  return n.slice(0, 200);
}

export type CargaRepisa = {
  productos: ProductoImportable[];
  /** id del renglón por `fila` del producto (1-based, como `fila` del importador). */
  idPorFila: Record<number, string>;
  fuera: { id: string; nombre: string; problemas: string[] }[];
};

const precioDe = (txt: string): { sinIgv: number; publico: number } | null => {
  const p = leerPrecio(txt);
  return tiene(p) ? { sinIgv: sinIgvDmDesdeVentaPublicaDm(p.dm), publico: p.dm } : null;
};

/**
 * Arma la carga de una repisa para el importador con los renglones listos; los que tienen problemas quedan
 * fuera. Ya no pasa por una hoja CSV: la hoja trae un solo lote y una sola presentación extra, y un renglón
 * de la repisa trae varios lotes y hasta dos (blíster y caja). La escritura sigue siendo la del importador.
 */
export function armarCargaRepisa(items: ItemIngreso[], repisa: string, hoy: string): CargaRepisa {
  const productos: ProductoImportable[] = [];
  const idPorFila: Record<number, string> = {};
  const fuera: CargaRepisa["fuera"] = [];
  const ubicacion = repisa.trim().slice(0, MAX_UBICACION) || null;
  const nulo = (s: string | null) => (s && s.trim() ? s.trim().slice(0, 200) : null);
  for (const it of items) {
    const a = analizarItem(it, items, hoy);
    if (a.problemas.length) {
      fuera.push({ id: it.id, nombre: it.nombre || it.gtin || "(sin nombre)", problemas: a.problemas });
      continue;
    }
    const m = moldeItem(it)!;
    const caja = precioDe(it.precio_caja);
    let base: { sinIgv: number; publico: number } | null;
    const presentaciones: BlisterImportable[] = [];
    if (m.cuentaPor === "unidad") {
      // La base es la tableta: lleva precio solo si se vende suelta. El código de barras es el de la caja.
      base = it.suelto === true ? precioDe(it.precio_unidad) : null;
      if (m.blister) {
        const bl = precioDe(it.precio_blister);
        presentaciones.push({ nombre: `Blíster x${m.blister}`, factor: m.blister, gtin: null, precioSinIgvDm: bl?.sinIgv ?? null, precioVentaPublicaDm: bl?.publico ?? null });
      }
      presentaciones.push({ nombre: `Caja x${m.caja}`, factor: m.caja, gtin: it.gtin, precioSinIgvDm: caja?.sinIgv ?? null, precioVentaPublicaDm: caja?.publico ?? null });
    } else {
      base = caja; // la caja entera es la unidad, con su código
    }
    const lotes = it.lotes.map((l, i) => ({ numero: l.lote.trim(), vencimiento: a.lotes[i]!.venceIso!, unidades: a.lotes[i]!.unidades }));
    const fila = productos.length + 1;
    productos.push({
      fila,
      nombre: it.nombre.trim().slice(0, 200),
      nombreNorm: normalizarNombre(it.nombre),
      gtin: m.cuentaPor === "unidad" ? null : it.gtin,
      laboratorio: nulo(it.laboratorio),
      principioActivo: nulo(it.principio_activo),
      categoria: nulo(it.forma),
      presentacionTexto: nulo(it.presentacion),
      requiereReceta: it.requiere_receta ? 1 : 0,
      esCronico: 0,
      precioCompraDm: null,
      precioSinIgvDm: base?.sinIgv ?? null,
      precioVentaPublicaDm: base?.publico ?? null,
      stockInicial: a.unidades,
      stockMinimo: 0,
      lotes,
      presentaciones,
      cuentaPor: m.cuentaPor,
      ubicacion,
    });
    idPorFila[fila] = it.id;
  }
  return { productos, idPorFila, fuera };
}
